import { clearSessionHint, markSessionHint } from './session.js';

let sessionExpiredHandled = false;

/** Call after a successful login so a later 401 can show the gate again. */
export function resetSessionExpiredFlag() {
  sessionExpiredHandled = false;
}

const DEFAULT_TIMEOUT_MS = 20000;
const GET_RETRY_DELAY_MS = 400;

function isTransientNetworkError(err) {
  return err && (err.name === 'TypeError' || err.name === 'AbortError');
}

async function fetchWithTimeout(path, opts, timeoutMs) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(path, { ...opts, signal: opts.signal || ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
}

export async function api(path, options = {}) {
  const { timeout = DEFAULT_TIMEOUT_MS, retry, ...rest } = options;
  const opts = {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(rest.headers || {}) },
    ...rest,
  };
  if (opts.body && typeof opts.body === 'object' && !(opts.body instanceof FormData)) {
    opts.body = JSON.stringify(opts.body);
  }
  const method = String(opts.method || 'GET').toUpperCase();
  // Reads are safe to retry once on a dropped connection / timeout; writes are not.
  const retries = retry ?? (method === 'GET' ? 1 : 0);

  let res;
  for (let attempt = 0; ; attempt++) {
    try {
      res = await fetchWithTimeout(path, opts, timeout);
      break;
    } catch (err) {
      if (attempt < retries && isTransientNetworkError(err) && navigator.onLine !== false) {
        await new Promise((r) => setTimeout(r, GET_RETRY_DELAY_MS * (attempt + 1)));
        continue;
      }
      const e = new Error(err?.name === 'AbortError' ? 'Request timed out — check your connection' : 'Network error — check your connection');
      e.cause = err;
      e.status = 0;
      throw e;
    }
  }
  let data = null;
  const text = await res.text();
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { error: text || 'Invalid response' };
  }
  if (!res.ok) {
    const err = new Error((data && data.error) || res.statusText || 'Request failed');
    err.status = res.status;
    err.data = data;
    if (res.status === 401) {
      clearSessionHint();
      if (!path.includes('/api/login') && !sessionExpiredHandled) {
        sessionExpiredHandled = true;
        if (typeof window.__myskyOn401 === 'function') window.__myskyOn401();
      }
    }
    throw err;
  }
  if (path === '/api/me' || path.endsWith('/api/login')) {
    markSessionHint();
  }
  return data;
}
